import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { DrumTimePickerComponent } from '../Time picker/time-picker.component';
import { CustomCalendarComponent } from '../calendar/custom-calendar.component';

interface Option {
  label: string;
  value: any;
  description?: string | string[] | any;
}

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    NgFor,
    FormsModule,
    DrumTimePickerComponent,
    CustomCalendarComponent,
  ],
})
export class InputComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input() label: string = '';
  @Input() subLabel: string = '';
  @Input() placeholder: string = '';
  @Input() type: string = 'text';
  @Input() lineRow: string = '3';
  @Input() min: string | null = null;
  @Input() max: string | null = null;
  @Input() control?: AbstractControl | null;
  @Input() required: boolean = false;
  @Input() dateType: boolean = false;
  @Input() options: Option[] = [];
  @Input() context: 'Normal' | 'default' = 'default';
  @Input() showAddNew: boolean = false;
  @Input() addNewText: string = '';
  @Input() customErrorMessages: { [key: string]: string } = {};
  @Input() fieldName: string = '';
  @Input() disabled: boolean = false;
  @Input() optional: boolean = false;
  @Input() labelGap: boolean = false;
  @Input() maxLength: number = 200;
  @Input() disableWeekends: boolean = false;
  @Input() textareaMaxLength: number = 1000;
  @Output() addNewClicked = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<any>();
  @Output() dateChange = new EventEmitter<any>();
  @Input() rangeStartDate: string | null = null;
  @Input() rangeEndDate: string | null = null;
  @Input() isEndDateCalendar: boolean = false;
  @Input() bgColor: string = '#000000';
  @Input() bgColorCondition: { [key: string]: string } = {};
  @ViewChild('dropdownOptions') dropdownOptionsRef!: ElementRef<HTMLDivElement>;
  @ViewChild('dateInputRef') dateInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('fromTimeInput') fromTimeInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('toTimeInput') toTimeInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput', { static: false })
  searchInputRef!: ElementRef<HTMLInputElement>;

  private isUpdatingValue: boolean = false;
  // FIX: timestamp to guard against mobile tap race conditions
  private _dropdownOpenedAt: number = 0;

  searchText: string = '';
  value: any = '';
  isFocused: boolean = false;
  dropdownOpen: boolean = false;
  formattedDate: string = '';
  fromTime: string = '';
  toTime: string = '';
  todayDate: string = '';
  fromTimeDisplay: string = '';
  toTimeDisplay: string = '';

  showCalendar: boolean = false;
  calendarInitialViewDate: string | null = null;
  calendarAnchorRect: DOMRect | null = null;

  selectedOptionIndex: number = -1;
  private lastFilteredOptionsLength: number = 0;

  private onChange = (value: any) => {};
  private onTouched = () => {};
  private boundScrollHandler: any;
  viewYear: any;
  private controlStatusSubscription?: Subscription;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit() {
    if (this.type === 'time') {
      this.syncNativeInputValues();
    }
  }

  private parseISODate(
    iso: string,
  ): { year: number; month: number; day: number } | null {
    if (!iso || typeof iso !== 'string') return null;
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return { year: +match[1], month: +match[2] - 1, day: +match[3] };
  }

  formatDateForDisplay(): void {
    if (this.type === 'date') {
      if (!this.value || this.value === '') {
        this.formattedDate = '';
      } else if (
        typeof this.value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(this.value)
      ) {
        const [year, month, day] = this.value.split('-');
        this.formattedDate = `${day}-${month}-${year}`;
      } else {
        const d = new Date(this.value);
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          this.formattedDate = `${day}-${month}-${d.getFullYear()}`;
        } else {
          this.formattedDate = '';
        }
      }
    }
    this.cdr.detectChanges();
  }

  toInputDate(val: any): string {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
      return val.substring(0, 10);
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  openCalendar(): void {
    if (this.type === 'date' && !this.disabled) {
      const wrapper = this.elementRef.nativeElement.querySelector(
        '.date-input-wrapper',
      );
      this.calendarAnchorRect = wrapper
        ? wrapper.getBoundingClientRect()
        : null;
      if (
        this.value &&
        !(
          typeof this.value === 'string' &&
          /^\d{4}-\d{2}-\d{2}$/.test(this.value)
        )
      ) {
        const date = new Date(this.value);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          this.value = `${year}-${month}-${day}`;
          this.onChange(this.value);
          this.formatDateForDisplay();
        }
      }

      // For end-date calendars with no value yet, open to the same month
      // as the range start date so the user doesn't have to navigate back.
      if (this.isEndDateCalendar && !this.value && this.rangeStartDate) {
        this.calendarInitialViewDate = this.rangeStartDate;
      } else {
        this.calendarInitialViewDate = null;
      }

      this.showCalendar = true;
      this.cdr.detectChanges();
    }
  }

  onCalendarDateSelected(iso: string): void {
    if (!iso) {
      this.value = '';
      this.formattedDate = '';
      this.onChange(null);
      if (this.control) {
        this.control.setValue(null);
        this.control.markAsDirty();
        this.control.markAsTouched();
      }
      this.dateChange.emit('');
    } else {
      this.value = iso;
      this.formatDateForDisplay();
      this.onChange(iso);
      if (this.control) {
        this.control.setValue(iso);
        this.control.markAsDirty();
      }
      this.dateChange.emit(iso);
    }
    this.cdr.detectChanges();
  }

  closeCalendar(): void {
    this.showCalendar = false;
    this.onTouched();
    this.control?.markAsTouched();
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    this.todayDate = `${yyyy}-${mm}-${dd}`;

    if (this.control) {
      this.controlStatusSubscription?.unsubscribe();
      this.controlStatusSubscription = this.control.statusChanges.subscribe(
        () => {
          this.disabled = this.control?.disabled ?? false;
          if (this.type === 'select') {
            this.searchText = this.getLabel(this.control?.value) || '';
          }
          this.cdr.markForCheck();
        },
      );

      if (this.type === 'time') {
        const val = this.control.value || '';
        if (typeof val === 'string') {
          this.parseFormattedTimeString(val);
        }
      } else {
        this.value = this.control.value || '';
        this.formatDateForDisplay();
        if (this.type === 'select') {
          this.searchText = this.getLabel(this.value) || '';
        }
      }

      this.control.valueChanges.subscribe((val) => {
        if (this.type === 'select') {
          const label = this.getLabel(val);
          this.searchText = label || '';
        }
        if (this.type === 'time') {
          if (typeof val === 'string') {
            this.parseFormattedTimeString(val);
          }
        } else {
          this.value = val || '';
          this.formatDateForDisplay();
        }
        this.cdr.markForCheck();
      });

      this.disabled = this.control.disabled;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] && this.control) {
      if (this.type === 'time') {
        const val = this.control.value || '';
        if (typeof val === 'string') {
          this.parseFormattedTimeString(val);
        }
      } else {
        this.value = this.control.value || '';
        this.formatDateForDisplay();
        if (this.type === 'select') {
          this.searchText = this.getLabel(this.value) || '';
        }
      }
      this.disabled = this.control.disabled;
    }
    this.cdr.detectChanges();

    if (changes['options'] && this.options && this.type === 'select') {
      if (this.value || this.control?.value) {
        const currentValue = this.value || this.control?.value;
        const label = this.getLabel(currentValue);
        if (label) {
          this.searchText = label;
        }
      }
      this.cdr.detectChanges();
    }

    if (changes['disabled']) {
      this.setDisabledState(this.disabled);
    }
  }

  ngOnDestroy(): void {
    this.controlStatusSubscription?.unsubscribe();
    this.removeScrollListeners();
  }

  get filteredOptions() {
    if (!this.searchText) return this.options;
    const lower = this.searchText.toLowerCase();
    const filtered = this.options.filter((opt) =>
      (opt.label || '').toLowerCase().includes(lower),
    );
    if (filtered.length !== this.lastFilteredOptionsLength) {
      this.selectedOptionIndex = -1;
      this.lastFilteredOptionsLength = filtered.length;
    }
    return filtered;
  }

  clearSelect(event: MouseEvent): void {
    if (this.disabled) return;
    event.stopPropagation();
    this.updateValue(null);
    this.searchText = '';
    this.selectedOptionIndex = -1;
    this.dropdownOpen = false;
    this.removeScrollListeners();
    this.selectionChange.emit({ value: null });
  }

  get isInvalid(): boolean {
    return (
      !!this.control &&
      this.control.invalid &&
      (this.control.touched || this.control.dirty)
    );
  }

  get errorMessage(): string {
    if (!this.control || !this.control.errors) return '';
    const errors = this.control.errors;
    if (errors['dateRange']) {
      if (this.customErrorMessages && this.customErrorMessages['dateRange']) {
        return this.customErrorMessages['dateRange'];
      }
      return 'End date must be after start date';
    }
    if (this.fieldName === 'postcode' && errors['pattern']) {
      return 'Postcode must be a 4-digit number.';
    }
    for (const errorKey in errors) {
      if (this.customErrorMessages && this.customErrorMessages[errorKey]) {
        return this.customErrorMessages[errorKey];
      }
    }
    if (errors['partial']) {
      if (this.customErrorMessages && this.customErrorMessages['partial']) {
        return this.customErrorMessages['partial'];
      }
      return 'Please enter start and end time.';
    }
    if (errors['required']) return 'This field is required.';
    if (errors['email']) return 'Invalid email address.';
    if (
      errors['pattern'] &&
      errors['pattern']?.requiredPattern ===
        '/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$/'
    ) {
      return 'Please enter a valid email address.';
    }
    if (errors['minlength']) {
      return `Minimum length is ${errors['minlength'].requiredLength} characters.`;
    }
    if (errors['maxlength']) {
      return `Maximum length is ${errors['maxlength'].requiredLength} characters.`;
    }
    if (errors['pattern']) return 'Invalid format.';
    return 'Invalid input.';
  }

  onInput(event: any): void {
    if (this.disabled) return;
    let value = event.target.value;
    const maxLen =
      this.type === 'textarea' ? this.textareaMaxLength : this.maxLength;
    if (value && value.length > maxLen) {
      value = value.substring(0, maxLen);
      event.target.value = value;
    }
    this.updateValue(value);
    if (this.type === 'date') {
      this.formatDateForDisplay();
      this.dateChange.emit(value);
    }
  }

  onTextareaInput(event: any): void {
    if (this.disabled) return;
    let value = event.target.value;
    if (value && value.length > this.textareaMaxLength) {
      value = value.substring(0, this.textareaMaxLength);
      event.target.value = value;
    }
    this.updateValue(value);
  }

  getCalendarYearRange(): number[] {
    if (this.dateType) {
      const maxDate = this.getResolvedMaxDate();
      const currentYear = maxDate
        ? (this.parseISODate(maxDate)?.year ?? new Date().getFullYear())
        : new Date().getFullYear();
      const startYear = 1990;
      const endYear = currentYear;
      return Array.from(
        { length: endYear - startYear + 1 },
        (_, i) => startYear + i,
      );
    } else {
      const base = this.viewYear || new Date().getFullYear();
      return Array.from({ length: 40 }, (_, i) => base - 15 + i);
    }
  }

  onFocus(): void {
    if (this.disabled) return;
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
    if (this.value || this.control?.dirty) {
      this.onTouched();
    }
    if (this.type === 'select' && this.value) {
      this.searchText = this.getLabel(this.value);
    }
  }

  onFromTimeChange(event: any): void {
    if (this.disabled) return;
    const value = event;
    this.fromTime = value;
    this.fromTimeDisplay = value;
    this.updateTimeValue();
  }

  onToTimeChange(event: any): void {
    if (this.disabled) return;
    const value = event;
    this.toTime = value;
    this.toTimeDisplay = value;
    this.updateTimeValue();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if (!this.dropdownOpen) {
      this.openDropdown();
      return;
    }
    const filteredOptions: any = this.filteredOptions;
    const totalOptions = filteredOptions.length + (this.showAddNew ? 1 : 0);
    if (totalOptions === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedOptionIndex =
          (this.selectedOptionIndex + 1) % totalOptions;
        this.scrollToSelectedOption();
        this.cdr.detectChanges();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedOptionIndex =
          this.selectedOptionIndex <= 0
            ? totalOptions - 1
            : this.selectedOptionIndex - 1;
        this.scrollToSelectedOption();
        this.cdr.detectChanges();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedOptionIndex >= 0) {
          if (
            this.showAddNew &&
            this.selectedOptionIndex === filteredOptions.length
          ) {
            this.onAddNew();
          } else if (this.selectedOptionIndex < filteredOptions.length) {
            this.selectOption(filteredOptions[this.selectedOptionIndex].value);
          }
        } else if (filteredOptions.length === 1) {
          this.selectOption(filteredOptions[0].value);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  private scrollToSelectedOption(): void {
    setTimeout(() => {
      const optionsContainer =
        this.dropdownOptionsRef?.nativeElement?.querySelector(
          '.scrollable-options',
        );
      const activeOption =
        this.dropdownOptionsRef?.nativeElement?.querySelector(
          '.custom-option.active',
        );
      if (optionsContainer && activeOption) {
        const containerTop = optionsContainer.scrollTop;
        const containerHeight = optionsContainer.clientHeight;
        const optionTop = (activeOption as HTMLElement).offsetTop;
        const optionHeight = (activeOption as HTMLElement).clientHeight;
        if (optionTop < containerTop) {
          optionsContainer.scrollTop = optionTop;
        } else if (optionTop + optionHeight > containerTop + containerHeight) {
          optionsContainer.scrollTop =
            optionTop + optionHeight - containerHeight;
        }
      }
    });
  }

  private formatTimeForDisplay(time: string): string {
    if (!time) return '';
    const parts = time.split(':');
    if (parts.length !== 2) return time;
    return time;
  }

  private updateTimeValue(): void {
    let formattedValue = '';

    if (this.fromTime && this.toTime) {
      if (this.control) {
        const errors = { ...this.control.errors };
        delete errors['required'];
        delete errors['missingStart'];
        delete errors['missingEnd'];
        if (this.isEndTimeBeforeStartTime()) {
          errors['timeRange'] = true;
        } else {
          delete errors['timeRange'];
        }
        this.control.setErrors(
          Object.keys(errors).length === 0 ? null : errors,
        );
      }
      formattedValue = `${this.formatTimeForDisplay(this.fromTime)} to ${this.formatTimeForDisplay(this.toTime)}`;
    } else if (this.fromTime && !this.toTime) {
      formattedValue = `${this.formatTimeForDisplay(this.fromTime)} to `;
      if (this.control) {
        this.control.setValue(formattedValue, { emitEvent: false });
        this.control.markAsDirty();
      }
    } else if (!this.fromTime && this.toTime) {
      formattedValue = ` to ${this.formatTimeForDisplay(this.toTime)}`;
      if (this.control) {
        const errors = { ...this.control.errors };
        delete errors['required'];
        delete errors['missingEnd'];
        delete errors['timeRange'];
        errors['missingStart'] = true;
        this.control.setErrors(errors);
      }
    } else {
      formattedValue = '';
      if (this.control) {
        const errors = { ...this.control.errors };
        delete errors['missingStart'];
        delete errors['missingEnd'];
        delete errors['timeRange'];
        errors['required'] = true;
        this.control.setErrors(errors);
      }
    }

    this.value = formattedValue;
    this.onChange(formattedValue);
    if (this.control) {
      this.control.setValue(formattedValue, {
        emitEvent: false,
        emitModelToViewChange: false,
      });
      this.control.markAsDirty({ onlySelf: true });
    }
    this.syncNativeInputValues();
    this.cdr.detectChanges();
  }

  private isEndTimeBeforeStartTime(): boolean {
    if (!this.fromTime || !this.toTime) return false;
    const [fromHours, fromMinutes] = this.fromTime.split(':').map(Number);
    const [toHours, toMinutes] = this.toTime.split(':').map(Number);
    return toHours * 60 + toMinutes <= fromHours * 60 + fromMinutes;
  }

  onTimeBlur(): void {
    if (this.control) {
      this.control.markAsTouched();
      this.onTouched();
    }
    this.cdr.detectChanges();
  }

  shouldShowError(): boolean {
    if (!this.control || this.disabled) return false;
    return this.control.invalid && (this.control.touched || this.control.dirty);
  }

  getSingleErrorMessage(): string {
    if (!this.control || !this.control.errors) return '';
    const errors = this.control.errors;
    if (errors['timeRange'])
      return (
        this.customErrorMessages?.['timeRange'] ||
        'End time must be after start time'
      );
    if (errors['required'])
      return (
        this.customErrorMessages?.['required'] ||
        'Please enter start and end time.'
      );
    if (errors['missingStart']) return 'Please enter a start time';
    if (errors['missingEnd']) return 'Please enter an end time';
    if (errors['invalidFormat'])
      return (
        this.customErrorMessages?.['invalidFormat'] || 'Invalid time format'
      );
    return 'Please enter valid time';
  }

  private syncNativeInputValues(): void {
    setTimeout(() => {
      if (this.fromTimeInputRef?.nativeElement) {
        this.fromTimeInputRef.nativeElement.value = this.fromTime || '';
      }
      if (this.toTimeInputRef?.nativeElement) {
        this.toTimeInputRef.nativeElement.value = this.toTime || '';
      }
    }, 0);
  }

  writeValue(value: any): void {
    if (this.isUpdatingValue) return;
    this.isUpdatingValue = true;
    try {
      if (this.type === 'time') {
        if (!value || value === '') {
          this.fromTime = '';
          this.toTime = '';
          this.value = '';
        } else if (typeof value === 'string') {
          this.parseFormattedTimeString(value);
          this.value = value;
        } else {
          this.fromTime = '';
          this.toTime = '';
          this.value = '';
        }
        this.fromTimeDisplay = this.formatTimeForDisplay(this.fromTime);
        this.toTimeDisplay = this.formatTimeForDisplay(this.toTime);
        this.syncNativeInputValues();
      } else {
        if (value instanceof Date && !isNaN(value.getTime())) {
          const year = value.getFullYear();
          const month = String(value.getMonth() + 1).padStart(2, '0');
          const day = String(value.getDate()).padStart(2, '0');
          this.value = `${year}-${month}-${day}`;
        } else if (value && typeof value === 'string' && value.includes('T')) {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            this.value = `${year}-${month}-${day}`;
          } else {
            this.value = value ?? '';
          }
        } else {
          this.value = value ?? '';
        }
        this.formatDateForDisplay();
        if (this.type === 'select') {
          this.searchText = this.getLabel(this.value) || '';
        }
      }
      this.cdr.detectChanges();
    } finally {
      this.isUpdatingValue = false;
    }
  }

  private parseFormattedTimeString(formattedValue: string): void {
    if (!formattedValue || formattedValue.trim() === '') {
      this.fromTime = '';
      this.toTime = '';
      this.value = '';
      return;
    }
    const parts = formattedValue.split(' to ');
    if (parts.length === 2) {
      const fromPart = parts[0].trim();
      this.fromTime = fromPart ? this.parseDisplayTimeToValue(fromPart) : '';
      const toPart = parts[1].trim();
      this.toTime = toPart ? this.parseDisplayTimeToValue(toPart) : '';
    } else {
      this.fromTime = '';
      this.toTime = '';
    }
    this.fromTimeDisplay = this.formatTimeForDisplay(this.fromTime);
    this.toTimeDisplay = this.formatTimeForDisplay(this.toTime);
    this.updateTimeValue();
  }

  private parseDisplayTimeToValue(displayTime: string): string {
    if (!displayTime || displayTime.trim() === '') return '';
    return displayTime;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this._dropdownOpenedAt = Date.now();
        this.searchText = '';
        this.selectedOptionIndex = -1;
        this.positionDropdown();
        this.addScrollListeners();
        this.focusSearchInput();
      } else {
        this.removeScrollListeners();
        if (this.value) {
          this.searchText = this.getLabel(this.value);
        }
      }
    }
  }

  // FIX 1: stamp _dropdownOpenedAt so the race-condition click/touchend
  // that fired to open the dropdown cannot immediately close it.
  openDropdown() {
    if (!this.disabled) {
      this.dropdownOpen = true;
      this._dropdownOpenedAt = Date.now();
      this.searchText = '';
      this.selectedOptionIndex = -1;
      this.positionDropdown();
      this.addScrollListeners();
      this.focusSearchInput();
    }
  }

  closeDropdown() {
    this.dropdownOpen = false;
    this.selectedOptionIndex = -1;
    this.removeScrollListeners();
    if (this.value) {
      this.searchText = this.getLabel(this.value);
    }
  }

  private focusSearchInput() {
    setTimeout(() => {
      if (this.searchInputRef?.nativeElement) {
        this.searchInputRef.nativeElement.focus();
      }
    });
  }

  private addScrollListeners() {
    this.boundScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.boundScrollHandler, true);
    document.addEventListener('scroll', this.boundScrollHandler, true);
  }

  private removeScrollListeners() {
    window.removeEventListener('scroll', this.boundScrollHandler, true);
    document.removeEventListener('scroll', this.boundScrollHandler, true);
  }

  // FIX 2: ignore scroll events for 400 ms after opening (virtual keyboard
  // animation triggers a scroll/resize that was closing the dropdown).
  private onScroll(event: Event): void {
    if (!this.dropdownOpen) return;

    if (Date.now() - this._dropdownOpenedAt < 400) return;

    const scrollTarget = event.target as Node;
    const dropdownEl = this.dropdownOptionsRef?.nativeElement;
    const hostEl = this.elementRef.nativeElement;
    if (dropdownEl?.contains(scrollTarget) || hostEl.contains(scrollTarget))
      return;

    this.ngZone.run(() => {
      this.dropdownOpen = false;
      this.selectedOptionIndex = -1;
      if (this.value) {
        this.searchText = this.getLabel(this.value);
      }
      this.cdr.markForCheck();
      this.removeScrollListeners();
    });
  }

  // FIX 2: same timestamp guard for focus (mobile fires focus → opens dropdown,
  // then immediately closes it via onSearchInputFocus before the previous
  // document:click guard was set).
  onSearchInputFocus() {
    if (!this.disabled) {
      this.dropdownOpen = true;
      this._dropdownOpenedAt = Date.now();
      this.selectedOptionIndex = -1;
      this.positionDropdown();
      this.addScrollListeners();
    }
  }

  onSearchInput() {
    if (!this.disabled) {
      this.dropdownOpen = true;
      this.selectedOptionIndex = -1;
    }
  }

  selectOption(value: any): void {
    if (this.disabled) return;
    this.updateValue(value);
    this.searchText = this.getLabel(value) || '';
    this.selectedOptionIndex = -1;
    this.dropdownOpen = false;
    this.removeScrollListeners();
    this.selectionChange.emit({ value: value });
  }

  private updateValue(value: any): void {
    this.value = value;
    this.onChange(value);
    if (this.control) {
      this.control.setValue(value);
      this.control.markAsDirty();
    }
    if (this.type === 'date') {
      this.formatDateForDisplay();
    }
  }

  getLabel(value: any): string {
    if (value == null || !this.options?.length) return '';
    const selected = this.options.find((opt) => opt.value === value);
    return selected?.label || '';
  }

  onAddNew(): void {
    if (this.disabled) return;
    this.dropdownOpen = false;
    this.selectedOptionIndex = -1;
    this.removeScrollListeners();
    this.addNewClicked.emit();
  }

  getBackgroundColor(): string {
    if (this.disabled) {
      return 'rgba(0, 0, 0, 0.6)';
    }
    if (this.bgColorCondition[this.fieldName]) {
      return this.bgColorCondition[this.fieldName];
    }
    return this.bgColor;
  }

  // FIX 3: positionDropdown with 100 ms delay (was 0) so measurements happen
  // after the virtual keyboard has shifted the layout.
  private positionDropdown(): void {
    setTimeout(() => {
      const selectDisplay = this.elementRef.nativeElement.querySelector(
        '.custom-select-display',
      );
      const dropdown =
        this.elementRef.nativeElement.querySelector('.custom-options');
      if (selectDisplay && dropdown) {
        const rect = selectDisplay.getBoundingClientRect();
        const dropdownHeight = 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        dropdown.style.position = 'fixed';
        dropdown.style.left = rect.left + 'px';
        dropdown.style.width = rect.width + 'px';
        if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
          dropdown.style.top = rect.bottom + 'px';
          dropdown.style.bottom = 'auto';
        } else {
          dropdown.style.bottom = window.innerHeight - rect.top + 8 + 'px';
          dropdown.style.top = 'auto';
        }
      }
    }, 100);
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:touchend', ['$event'])
  onDocumentClick(event: MouseEvent | TouchEvent): void {
    if (Date.now() - this._dropdownOpenedAt < 300) return;
    const target =
      'changedTouches' in event &&
      (event as TouchEvent).changedTouches.length > 0
        ? document.elementFromPoint(
            (event as TouchEvent).changedTouches[0].clientX,
            (event as TouchEvent).changedTouches[0].clientY,
          )
        : (event as MouseEvent).target;

    const clickedInside = this.elementRef.nativeElement.contains(target);
    if (!clickedInside && this.dropdownOpen) {
      this.dropdownOpen = false;
      this.selectedOptionIndex = -1;
      if (this.value) {
        this.searchText = this.getLabel(this.value);
      }
      this.cdr.markForCheck();
      this.removeScrollListeners();
    }
  }

  openTimePicker(event: MouseEvent, field: 'from' | 'to'): void {
    if (this.disabled) return;
    event.stopPropagation();
    event.preventDefault();
    const inputRef =
      field === 'from' ? this.fromTimeInputRef : this.toTimeInputRef;
    if (inputRef?.nativeElement) {
      inputRef.nativeElement.showPicker?.();
    }
  }

  getRemainingChars(): number {
    if (!this.value)
      return this.type === 'textarea' ? this.textareaMaxLength : this.maxLength;
    const maxLen =
      this.type === 'textarea' ? this.textareaMaxLength : this.maxLength;
    return maxLen - (this.value.toString().length || 0);
  }

  getResolvedMaxDate(): string | null {
    if (this.max) {
      return this.max;
    }

    return this.dateType ? this.todayDate : null;
  }
}
